import PropTypes from 'prop-types'
import { memo, useEffect, useRef, useState } from 'react'
import CheckBox from 'components/form/CheckBox'
import UrlInput from 'components/form/URLInput'
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  setMobileUrl,
  setPcUrl,
  setUnifiedUrl,
  setUrlIndividual,
} from 'features/channel/add/channelAddSlice'
import Button from 'components/form/Button'
import Table from 'components/table/Table'
import ErrorText from 'components/form/ErrorText'
import { isPresent, noop } from 'utils'
import checkUnifiedUrl from 'features/channel/add/thunk/checkUnifiedUrl'
import checkMobileUrl from 'features/channel/add/thunk/checkMobileUrl'
import checkPcUrl from 'features/channel/add/thunk/checkPcUrl'
import { useAlert } from 'features/dialog/Alert'
import HelpTooltip from 'components/tooltip/HelpTooltip'
import { BizChannelTypes } from '../../../Codes'
import { isNil } from 'lodash'

export const hasURL = url => !isNil(url) && url.trim() !== ''
const fixLineBreak = value => value.replace(/\n/g, '')
const SiteURL = memo(
  ({
    unifiedUrlError,
    mobileUrlError,
    pcUrlError,

    unifiedUrlConnectionError,
    mobileUrlConnectionError,
    pcUrlConnectionError,

    handleUnifiedUrlError = noop,
    handleMobileUrlError = noop,
    handlePcUrlError = noop,

    isEditable,
    isExisting,
  }) => {
    const {
      bizChannelType,
      unifiedUrl,
      mobileUrl,
      pcUrl,
      isUnifiedUrlChecked,
      isMobileUrlChecked,
      isPcUrlChecked,
      isUrlIndividual,
      bizChannelId,
    } = useSelector(state => state.channel.channelAdd, shallowEqual)

    const dispatch = useDispatch()
    const checkUnifiedUrlRef = useRef()
    const checkMobileUrlRef = useRef()
    const checkPcUrlRef = useRef()

    const [isCheckingMobileUrl, setIsCheckingMobileUrl] = useState(false)
    const [isCheckingPcUrl, setIsCheckingPcUrl] = useState(false)
    const [isCheckingUnifiedUrl, setIsCheckingUnifiedUrl] = useState(false)
    const [SiteNotCheckedAlert, showSiteNotCheckedAlert] = useAlert()

    const handleSetUrlIndividual = isChecked =>
      dispatch(setUrlIndividual(isChecked))

    const handleCheckMobileUrl = async () => {
      setIsCheckingMobileUrl(true)
      const { error, payload } = await dispatch(checkMobileUrl())
      if (!isPresent(error) && !payload.result) {
        showSiteNotCheckedAlert()
      }
      setIsCheckingMobileUrl(false)
    }

    const handleCheckPcUrl = async () => {
      setIsCheckingPcUrl(true)
      const { error, payload } = await dispatch(checkPcUrl())
      if (!isPresent(error) && !payload.result) {
        showSiteNotCheckedAlert()
      }
      setIsCheckingPcUrl(false)
    }

    const handleCheckUnifiedUrl = async () => {
      setIsCheckingUnifiedUrl(true)
      const { error, payload } = await dispatch(checkUnifiedUrl())
      if (!isPresent(error) && !payload.result) {
        showSiteNotCheckedAlert()
      }
      setIsCheckingUnifiedUrl(false)
    }

    useEffect(() => {
      if (!isUrlIndividual || isExisting) return
      batch(() => {
        dispatch(setMobileUrl(unifiedUrl))
        dispatch(setPcUrl(unifiedUrl))
      })
    }, [dispatch, isUrlIndividual, unifiedUrl, isExisting])

    useEffect(() => {
      if (isUrlIndividual || isExisting) return
      dispatch(setUnifiedUrl([mobileUrl, pcUrl].find(hasURL) || null))
    }, [dispatch, isUrlIndividual, mobileUrl, isExisting, pcUrl])

    return (
      <Table.Row
        className="tr_url"
        error={
          isUrlIndividual ? mobileUrlError || pcUrlError : unifiedUrlError
        }>
        <th scope="row">
          <div className="inner_tbl">
            웹사이트 URL
            <HelpTooltip>
              광고하고자 하는 웹사이트 주소를 입력합니다.
              <br />
              웹사이트의 모바일 최적화 상태에 따라 디바이스 별 URL 설정이 필요할
              수 있습니다.
            </HelpTooltip>
          </div>
        </th>
        <td>
          <div className="inner_tbl">
            <SiteNotCheckedAlert tile="사이트 연결 확인">
              사이트 연결이 확인되지 않습니다.
              <br />
              이와 같은 경우 사이트 추가 신청은 가능하나
              <br />
              심사 과정에서 보류 처리될 수 있습니다.
              <br />
            </SiteNotCheckedAlert>
            <CheckBox
              name="inpCheckSet2"
              label="모바일 / PC 개별 설정"
              checked={isUrlIndividual}
              disabled={
                isExisting || bizChannelType === BizChannelTypes.TALK_CHANNEL
              }
              onCheckedChange={handleSetUrlIndividual}
            />
            <HelpTooltip
              nowrap={false}
              className={
                'tooltip_basic ly_tooltip ly_tooltip_type2 tooltip_url'
              }>
              개별 설정 시 소재 및 키워드 랜딩 URL 또한 비즈채널에 등록된 것과
              동일한 디바이스만 입력 가능합니다.
              <ul className="list_tooltip">
                <li>
                  <span className="ico_dot"></span>웹사이트가 모바일/PC 모두
                  최적화된 상태라면 단일 등록이 가능합니다.
                </li>
                <li>
                  <span className="ico_dot"></span>웹사이트가 모바일/PC 별도
                  구분된 경우 개별 등록이 필요합니다.
                </li>
                <li>
                  <span className="ico_dot"></span>웹사이트가 모바일에
                  최적화되어 있지 않다면 PC URL만 등록하시기 바랍니다.
                </li>
              </ul>
            </HelpTooltip>
            {isUrlIndividual ? (
              <>
                <div className="group_item">
                  <strong className="sub_tit">모바일 URL</strong>
                  <UrlInput
                    focusOnError
                    error={
                      !isCheckingMobileUrl &&
                      (mobileUrlError || mobileUrlConnectionError)
                    }
                    placeholder="http:// 또는 https:// 형식의 정상적인 랜딩 URL을 입력하세요."
                    value={mobileUrl}
                    disabled={
                      !isEditable || (bizChannelId && mobileUrl === null)
                    }
                    onChange={e =>
                      dispatch(setMobileUrl(fixLineBreak(e.target.value)))
                    }
                    onError={handleMobileUrlError}
                  />

                  <Button
                    line
                    ref={checkMobileUrlRef}
                    onClick={handleCheckMobileUrl}
                    loading={isCheckingMobileUrl}
                    disabled={
                      !isEditable ||
                      !hasURL(mobileUrl) ||
                      !!mobileUrlError ||
                      !!isMobileUrlChecked
                    }>
                    {isMobileUrlChecked ? '확인완료' : '연결확인'}
                  </Button>
                </div>
                <ErrorText
                  error={
                    !isCheckingMobileUrl &&
                    (mobileUrlError || mobileUrlConnectionError)
                  }
                />
                <div className="group_item">
                  <strong className="sub_tit">PC URL</strong>
                  <UrlInput
                    focusOnError
                    error={
                      !isCheckingPcUrl && (pcUrlError || pcUrlConnectionError)
                    }
                    placeholder="http:// 또는 https:// 형식의 정상적인 랜딩 URL을 입력하세요."
                    value={pcUrl}
                    disabled={
                      !isEditable ||
                      bizChannelType === BizChannelTypes.TALK_CHANNEL ||
                      (bizChannelId && pcUrl === null)
                    }
                    onChange={e =>
                      dispatch(setPcUrl(fixLineBreak(e.target.value)))
                    }
                    onError={handlePcUrlError}
                  />
                  <Button
                    line
                    ref={checkPcUrlRef}
                    onClick={handleCheckPcUrl}
                    loading={isCheckingPcUrl}
                    disabled={
                      !isEditable ||
                      !hasURL(pcUrl) ||
                      !!pcUrlError ||
                      !!isPcUrlChecked
                    }>
                    {isPcUrlChecked ? '확인완료' : '연결확인'}
                  </Button>
                </div>
                <ErrorText
                  error={
                    !isCheckingPcUrl && (pcUrlError || pcUrlConnectionError)
                  }
                />
              </>
            ) : (
              <>
                <div className="group_item">
                  <UrlInput
                    focusOnError
                    error={
                      !isCheckingUnifiedUrl &&
                      (unifiedUrlError || unifiedUrlConnectionError)
                    }
                    placeholder="http:// 또는 https:// 형식의 정상적인 랜딩 URL을 입력하세요."
                    value={unifiedUrl}
                    disabled={!isEditable}
                    onChange={e =>
                      dispatch(setUnifiedUrl(fixLineBreak(e.target.value)))
                    }
                    onError={handleUnifiedUrlError}
                  />

                  <Button
                    line
                    ref={checkUnifiedUrlRef}
                    onClick={isUnifiedUrlChecked ? noop : handleCheckUnifiedUrl}
                    loading={isCheckingUnifiedUrl}
                    disabled={
                      !isEditable ||
                      !hasURL(unifiedUrl) ||
                      !!unifiedUrlError ||
                      !!isUnifiedUrlChecked
                    }>
                    {isUnifiedUrlChecked ? '확인완료' : '연결확인'}
                  </Button>
                </div>
                <ErrorText
                  error={
                    !isCheckingUnifiedUrl &&
                    (unifiedUrlError || unifiedUrlConnectionError)
                  }
                />
              </>
            )}
          </div>
        </td>
      </Table.Row>
    )
  }
)

SiteURL.displayName = 'SiteURL'
SiteURL.propTypes = {
  mobileUrlError: PropTypes.string,
  pcUrlError: PropTypes.string,
  unifiedUrlError: PropTypes.string,

  handleUnifiedUrlError: PropTypes.func,
  handleMobileUrlError: PropTypes.func,
  handlePcUrlError: PropTypes.func,

  unifiedUrlConnectionError: PropTypes.string,
  mobileUrlConnectionError: PropTypes.string,
  pcUrlConnectionError: PropTypes.string,
  isEditable: PropTypes.bool,
  isExisting: PropTypes.bool,
}
export default SiteURL
